import React, { useState } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import useResizeObserver from "@react-hook/resize-observer";
import { UI_TAG, UI_BODY_SEMIBOLD_SM_DF, UI_BODY_SM_DF, colors2, ButtonGhostV2 } from "oolib";
import { CaretDownIcon, ChartBarHorizontalIcon } from "@phosphor-icons/react";
import { DropdownSingle as DropdownSingleRaw } from "oolib/dist/v2/components/Dropdowns/DropdownSingle";
import { BarChart as BarChartRaw } from "../../../components/dataviz/BarChart";
import { useGetTypeCategoryAndTags } from "../../../components/inputs/DynamicTagsInputs/utils";
import { buildSubThemeColorIdxMap } from "@/components/analysisModules/comps/AnnotationPrevalence";
import { useGetTopicClusteringTree } from "@/utils/react-query-hooks/aiAnnotation";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { getAnnoMode } from "@/utils/getAnnoMode";
import { groupDocTopicsFromTree } from "./groupDocTopicsFromTree";
import { t } from "../theme";

const BarChart = BarChartRaw as React.FC<any>;
const DropdownSingle = DropdownSingleRaw as React.FC<any>;

// Outer padding lives on the sidebar <Card> (16px); this shell adds none.
const Box = styled.div`
  display: flex;
  flex-direction: column;
  // gap: 0.8rem;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

const Reveal = styled(motion.div)`
  overflow: hidden;
`;

// Cap the chart region; beyond this it scrolls. The animated height is clamped
// to this so the card never grows unbounded for subthemes with many tags.
const MAX_CHART_HEIGHT = 240;

const CHART_HEIGHT_TRANSITION = {
  type: "tween",
  duration: 0.27,
  ease: [0.32, 0.72, 0, 1],
} as const;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  padding-top: 0.8rem;
`;

// Animates its own height (driven by the measured chart content) so switching
// subthemes — which changes how many bars render — eases between sizes instead
// of snapping. Mirrors AnimatedTabs' PanelOuter. The bleed margin lives here so
// the chart still spans past the sidebar Card's 16px padding.
const ChartOuter = styled(motion.div)`
  position: relative;
  overflow: hidden;
  margin: 0 -16px -16px;
`;

const ChartScroll = styled.div`
  max-height: ${MAX_CHART_HEIGHT}px;
  overflow-y: auto;

  /* recharts renders its <svg> with tabindex="0"; clicking a bar focuses it
     and Chrome paints a focus ring around the whole chart. The descendant
     combinator (note the leading space) targets the inner svg, not this div. */
  &:focus,
  &:focus-visible,
  & *:focus,
  & *:focus-visible {
    outline: none;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.6rem;
  padding: 2.4rem 1rem;
  text-align: center;
`;

// Mirrors AnnotationExplorer's prevalence-tab select trigger
// (StyledToolbarSelectTrigger).
const SelectTrigger = styled.button`
  background: none;
  border: 1px solid ${colors2.grey20};
  border-radius: 4px;
  padding: 2.5px 5px;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  cursor: pointer;

  &:hover {
    background: ${colors2.grey5};
  }
`;

// Measures the live height of the rendered chart content via ResizeObserver,
// plus a layout-effect read so the first paint (and dep changes) is captured
// before the browser shows a stale value. Mirrors AnimatedTabs' useMeasureHeight.
function useMeasureHeight(deps: React.DependencyList = []) {
  const [el, setEl] = React.useState<HTMLDivElement | null>(null);
  const [height, setHeight] = React.useState(0);

  const setRef = React.useCallback((node: HTMLDivElement | null) => {
    if (node) setEl(node);
  }, []);

  useResizeObserver(el, (entry) => {
    const next = entry.contentRect.height;
    setHeight((prev) => (prev === next ? prev : next));
  });

  React.useLayoutEffect(() => {
    if (!el) return;
    const next = el.getBoundingClientRect().height;
    setHeight((prev) => (prev === next ? prev : next));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [el, ...deps]);

  return [setRef, height] as const;
}

export type SubThemeGroup = {
  subThemeId: string;
  subThemeDisplay: string;
  total: number;
  tags: any[];
};

export type SelectedTag = {
  subThemeId: string;
  subThemeLabel: string;
  _id: string;
  tagLabel: string;
  // Present on a topic bar: the annoKeys of this document's observations
  // under that sub-topic.
  annoKeys?: string[];
};

/**
 * Self-contained subtheme picker + prevalence bar chart. Derives this
 * document's prevalence groups + colour map straight from `contribution`, so it
 * can be dropped anywhere with just the contribution (sidebar card, AI panel, …)
 * — no host needs to compute the data. In split mode the picker also lists the
 * document's topics (from the clustering tree), each charting its sub-topics. Also owns the selected-subtheme state,
 * the derived dropdown options / active group, and the chart-height animation.
 * The tag/subTheme queries are react-query-cached, so multiple mounts don't
 * refetch. Renders nothing when the document has no grouped annotations.
 */
export const DistributionCardContent: React.FC<{
  contribution: any;
  activeTagId?: string;
  onSelectTag?: (tag: SelectedTag) => void;
}> = ({ contribution, activeTagId, onSelectTag }) => {
  const tagsQuery: any = useGetTypeCategoryAndTags({ tagTypeProp: "tags", enableDataFetch: true } as any);
  const subThemesQuery: any = useGetTypeCategoryAndTags({ tagTypeProp: "subThemes", enableDataFetch: true } as any);
  const tagDocs: any[] = Array.isArray(tagsQuery?.data) ? tagsQuery.data : [];
  const subThemeDocs: any[] = Array.isArray(subThemesQuery?.data) ? subThemesQuery.data : [];

  // Split mode: after the sub-themes (just Signals there), one dropdown entry
  // per topic this document has observations in, its sub-topics as the bars.
  // Read off the tenant's clustering tree — the same cached query the Topics
  // tab uses — which lists every observation with its documentId.
  const platformConfigs: any = useGetQueryData("platformConfigs");
  const isSplitMode = getAnnoMode(platformConfigs) === "split";
  const { data: treeData } = useGetTopicClusteringTree({ enabled: isSplitMode });
  const topicGroups = React.useMemo(
    () => (isSplitMode ? groupDocTopicsFromTree({ treeData, documentId: contribution?._id }) : []),
    [isSplitMode, treeData, contribution?._id],
  );

  const tagDocMap = React.useMemo(() => {
    const m: Record<string, any> = {};
    tagDocs.forEach((tg) => {
      m[tg._id] = tg;
    });
    return m;
  }, [tagDocs]);

  const annotationData = React.useMemo(() => {
    const data: any[] = contribution?.annotations?.tags?.tags?.data || [];
    return data.map((tg) => ({
      value: tg._id,
      display: tg.display || tagDocMap[tg._id]?.display || tg._id,
      count: Array.isArray(tg.fragments) ? tg.fragments.length : tg.count || 0,
    }));
  }, [contribution, tagDocMap]);

  const subThemeGroups: SubThemeGroup[] = React.useMemo(
    () => groupBySubTheme({ annotationData, tagDocs, subThemeDocs }),
    [annotationData, tagDocs, subThemeDocs],
  );
  const groups: SubThemeGroup[] = React.useMemo(
    () => (topicGroups.length ? [...subThemeGroups, ...topicGroups] : subThemeGroups),
    [subThemeGroups, topicGroups],
  );
  // Sub-theme colour slots first, then one per topic — the slot order the
  // Annotation Counts widget uses, so a topic wears the same colour here.
  const colorIdxMap = React.useMemo(() => {
    const map: Record<string, number> = { ...buildSubThemeColorIdxMap(subThemeDocs) };
    const offset = Object.keys(map).length;
    topicGroups
      .map((g) => g.subThemeId)
      .sort()
      .forEach((id, i) => {
        map[id] = offset + i;
      });
    return map;
  }, [subThemeDocs, topicGroups]);

  const [selected, setSelected] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (groups.length && !groups.some((g) => g.subThemeId === selected)) {
      setSelected(groups[0].subThemeId);
    }
  }, [groups, selected]);

  const options = groups.map((g) => ({ value: g.subThemeId, display: g.subThemeDisplay }));
  const activeGroup = groups.find((g) => g.subThemeId === selected) || groups[0];

  // Re-measure whenever the selected subtheme (and thus the chart's bar count)
  // changes so the outer can animate to the new height.
  const [setChartRef, chartHeight] = useMeasureHeight([activeGroup?.subThemeId]);

  // No grouped annotations. The sidebar host (DistributionCard) short-circuits
  // before reaching here, so this empty state only renders in the AI-panel tab,
  // where the Distribution tab is always present and needs something to show.
  if (!groups.length)
    return (
      <EmptyState onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <ChartBarHorizontalIcon size={24} weight="bold" color={colors2.grey40} />
        <UI_BODY_SM_DF style={{ color: colors2.grey60 }}>
          No distribution yet. Once this document has grouped annotations, their
          prevalence will show up here.
        </UI_BODY_SM_DF>
      </EmptyState>
    );

  return (
    <Container onClick={(e: React.MouseEvent) => e.stopPropagation()}>
      <DropdownSingle
        id="v3SubthemePrevalence"
        value={selected}
        onChange={(_k: string, v: string) => setSelected(v)}
        options={options}
        saveValueAsString
        popOutOfOverflowHiddenParent
        genCustomSelectComp={() => (
          <SelectTrigger type="button">
            <UI_TAG style={{ color: colors2.grey80 }}>
              {activeGroup?.subThemeDisplay ?? "Select subtheme"}
            </UI_TAG>
            <CaretDownIcon size={10} weight="bold" color={colors2.grey60} />
          </SelectTrigger>
        )}
      />
      <ChartOuter
        animate={{ height: chartHeight ? Math.min(chartHeight, MAX_CHART_HEIGHT) : "auto" }}
        transition={CHART_HEIGHT_TRANSITION}
      >
        <ChartScroll>
          <div ref={setChartRef}>
            {activeGroup && (
              <BarChart
                data={activeGroup.tags}
                valuePath="count"
                tooltipLabelsPath="display"
                labelPath="display"
                colorIdx={colorIdxMap[activeGroup.subThemeId] ?? 0}
                summarizeAfterIdx={Infinity}
                highlightValue={activeTagId}
                showCount
                onClick={(v: any) => {
                  const bar = activeGroup.tags.find((b: any) => String(b.value) === String(v.value));
                  onSelectTag?.({
                    subThemeId: activeGroup.subThemeId,
                    subThemeLabel: activeGroup.subThemeDisplay,
                    _id: v.value,
                    tagLabel: v.display ?? v.name,
                    // Topic bars: the page filters cards by these rather
                    // than by a tag id (topic membership lives on the tree,
                    // not on the annotation's tags).
                    ...(bar?.annoKeys ? { annoKeys: bar.annoKeys } : {}),
                  });
                }}
              />
            )}
          </div>
        </ChartScroll>
      </ChartOuter>
    </Container>
  );
};

/**
 * Single-document subtheme prevalence: pick a subTheme → bar chart of its tags
 * by how many times each is applied in THIS document.
 *
 * Reuses the AnnotationExplorer prevalence machinery (BarChart, the subTheme
 * colour map, the tag/subTheme doc hooks). The grouping is re-implemented here
 * (small) — the original `groupAnnotationDataBySubTheme` lives inside
 * AnnotationExplorerVizPanel, which we don't touch. Data comes from the doc's
 * own consolidated annotations (count = number of annotation fragments), so no
 * collection-wide endpoint is needed.
 */
const groupBySubTheme = ({ annotationData, tagDocs, subThemeDocs }: any) => {
  if (!annotationData?.length || !tagDocs?.length) return [];
  const subThemeDisplayMap: Record<string, string> = {};
  subThemeDocs.forEach((st: any) => {
    subThemeDisplayMap[st._id] = st.display;
  });
  const tagDocMap: Record<string, any> = {};
  tagDocs.forEach((tg: any) => {
    tagDocMap[tg._id] = tg;
  });

  const groups: Record<string, any> = {};
  const UNGROUPED = "__ungrouped__";
  annotationData.forEach((row: any) => {
    const tagDoc = tagDocMap[row.value];
    const firstSubTheme = tagDoc?.tags?.subThemes?.data?.[0];
    const key = firstSubTheme?._id || UNGROUPED;
    const display = firstSubTheme
      ? subThemeDisplayMap[firstSubTheme._id] || firstSubTheme.display || "Ungrouped"
      : "Ungrouped";
    if (!groups[key]) {
      groups[key] = { subThemeId: key, subThemeDisplay: display, total: 0, tags: [] };
    }
    groups[key].total += row.count || 0;
    groups[key].tags.push(row);
  });

  return Object.values(groups)
    .map((g: any) => ({
      ...g,
      tags: [...g.tags].sort((a, b) => (b.count || 0) - (a.count || 0)),
    }))
    .sort((a: any, b: any) => b.total - a.total);
};

export const DistributionCard: React.FC<{
  contribution: any;
  activeTagId?: string;
  onSelectTag?: (tag: SelectedTag) => void;
}> = ({ contribution, activeTagId, onSelectTag }) => {
  const tagsQuery: any = useGetTypeCategoryAndTags({ tagTypeProp: "tags", enableDataFetch: true } as any);
  const subThemesQuery: any = useGetTypeCategoryAndTags({ tagTypeProp: "subThemes", enableDataFetch: true } as any);
  const tagDocs: any[] = Array.isArray(tagsQuery?.data) ? tagsQuery.data : [];
  const subThemeDocs: any[] = Array.isArray(subThemesQuery?.data) ? subThemesQuery.data : [];
  const [expanded, setExpanded] = useState(true);

  const tagDocMap = React.useMemo(() => {
    const m: Record<string, any> = {};
    tagDocs.forEach((tg) => {
      m[tg._id] = tg;
    });
    return m;
  }, [tagDocs]);

  const annotationData = React.useMemo(() => {
    const data: any[] = contribution?.annotations?.tags?.tags?.data || [];
    return data.map((tg) => ({
      value: tg._id,
      display: tg.display || tagDocMap[tg._id]?.display || tg._id,
      count: Array.isArray(tg.fragments) ? tg.fragments.length : tg.count || 0,
    }));
  }, [contribution, tagDocMap]);

  const groups = React.useMemo(
    () => groupBySubTheme({ annotationData, tagDocs, subThemeDocs }),
    [annotationData, tagDocs, subThemeDocs],
  );

  if (!groups.length) return null; // no annotations in this doc → no widget

  return (
    <Box>
      <Header onClick={() => setExpanded((p) => !p)}>
        <TitleRow>
          <ChartBarHorizontalIcon size={16} weight="bold" color={t("grey80")} />
          <UI_BODY_SEMIBOLD_SM_DF style={{ color: t("grey80") }}>Distribution</UI_BODY_SEMIBOLD_SM_DF>
        </TitleRow>
        <ButtonGhostV2
          icon={expanded ? "CaretUp" : "CaretDown"}
          iconColor={t("grey80")}
          style={{ padding: 4 }}
        />
      </Header>
      <AnimatePresence initial={false}>
        {expanded && (
          <Reveal
            key="reveal"
            initial={{ height: 0 }}
            animate={{ height: "auto" }}
            exit={{ height: 0 }}
            transition={{ duration: 0.27, ease: [0.32, 0.72, 0, 1] }}
          >
            <DistributionCardContent
              contribution={contribution}
              activeTagId={activeTagId}
              onSelectTag={onSelectTag}
            />
          </Reveal>
        )}
      </AnimatePresence>
    </Box>
  );
};

export default DistributionCard;
